import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogHeader, DialogBody, DialogFooter, Button, Input, Spinner, List, ListItem, Checkbox,
} from '@material-tailwind/react';
import { SearchIcon, ArrowLeftIcon, ArrowRightIcon, PlusIcon, Trash2Icon, AlertTriangleIcon } from 'lucide-react';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { useGetUsers } from '../../custom-hooks/use-users';
import { useCreateManualInvoice } from '../../custom-hooks/use-invoices';
import useDebounce from '../../custom-hooks/useDebounce';
import { GetContractByUserId, GetInvoices } from '../../services/backend';
import { STATIC_USER_ROLES, VERIFICATION_TYPES, INVOICE_LINE_ITEM_TYPES, INVOICE_STATUSES } from '../../utils/enums';
import { convertToPresentableText, formatNumberWithCommas, Notify } from '../../utils/functions';

const STEP_LABELS = ['Customer', 'Contract', 'Bookings', 'Billing Period', 'Preview'];

const bookingLabel = (b) => {
    if (b.office) return `Office ${b.office?.code || b.office?.number || 'N/A'}`;
    if (b.seat) return `Seat ${b.seat?.seatCode || 'N/A'}`;
    return 'Booking';
};

const CreateInvoiceModal = ({ isOpen, onClose, presetCustomer = null, onSuccess }) => {
    const navigate = useNavigate();
    const createMutation = useCreateManualInvoice();

    const [step, setStep] = useState(presetCustomer ? 1 : 0);
    const [customer, setCustomer] = useState(presetCustomer);
    const [customerSearch, setCustomerSearch] = useState('');
    const debouncedCustomerSearch = useDebounce(customerSearch, 500);

    const [contracts, setContracts] = useState([]);
    const [contractsLoading, setContractsLoading] = useState(false);
    const [selectedContractId, setSelectedContractId] = useState('');
    const [selectedBookingIds, setSelectedBookingIds] = useState(new Set());
    // bookingId -> admin-entered override amount (string, as typed). Falls back to the
    // booking's computed rent (bookingRent) when not overridden.
    const [bookingAmounts, setBookingAmounts] = useState({});

    // bookingId -> invoiceNumber of an existing non-void invoice that already bills it
    // for the currently selected billing period.
    const [coveredBookings, setCoveredBookings] = useState(new Map());

    const [month, setMonth] = useState(moment().month() + 1);
    const [year, setYear] = useState(moment().year());
    const [extraLineItems, setExtraLineItems] = useState([]);
    const [dueDate, setDueDate] = useState('');
    const [notes, setNotes] = useState('');

    const { data: usersData, isLoading: isLoadingUsers } = useGetUsers({
        role: [STATIC_USER_ROLES.USER],
        _search: debouncedCustomerSearch,
        _limit: 50,
    });

    // Reset all state whenever the modal opens
    useEffect(() => {
        if (!isOpen) return;
        setStep(presetCustomer ? 1 : 0);
        setCustomer(presetCustomer || null);
        setCustomerSearch('');
        setContracts([]);
        setSelectedContractId('');
        setSelectedBookingIds(new Set());
        setBookingAmounts({});
        setCoveredBookings(new Map());
        setMonth(moment().month() + 1);
        setYear(moment().year());
        setExtraLineItems([]);
        setDueDate('');
        setNotes('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, presetCustomer]);

    // Load the customer's contracts once one is selected
    useEffect(() => {
        if (!isOpen || !customer?._id) return;
        setContractsLoading(true);
        GetContractByUserId(customer._id)
            .then(res => setContracts((res.data?.contracts || []).filter(c => !c.void?.isVoid)))
            .catch(() => setContracts([]))
            .finally(() => setContractsLoading(false));
    }, [isOpen, customer?._id]);

    // Find which non-void invoices already exist for this customer+period, so we can flag
    // bookings that would be double-billed — before creation, not just after.
    // `month`/`year` already hold their (current-month) default here even before Step 4 is
    // visited, so this is accurate for the common case and updates live if the period changes.
    useEffect(() => {
        if (!isOpen || !customer?._id || !month || !year) return;
        GetInvoices({ customer: customer._id, month, year, limit: 100 })
            .then(res => {
                const map = new Map();
                (res.data?.invoices || []).forEach(inv => {
                    if (inv.status === INVOICE_STATUSES.VOID) return;
                    (inv.lineItems || []).forEach(item => {
                        const bookingId = item.booking?._id || item.booking;
                        if (bookingId) map.set(String(bookingId), inv.invoiceNumber);
                    });
                });
                setCoveredBookings(map);
            })
            .catch(() => setCoveredBookings(new Map()));
    }, [isOpen, customer?._id, month, year]);

    const selectedContract = contracts.find(c => c._id === selectedContractId);
    const approvedBookings = (selectedContract?.bookings || []).filter(b => b.status === VERIFICATION_TYPES.APPROVED);
    const selectedBookings = approvedBookings.filter(b => selectedBookingIds.has(b._id));

    const isFirstMonthBooking = (b) => {
        const start = moment(b.startDate);
        return start.month() + 1 === Number(month) && start.year() === Number(year);
    };
    const bookingRent = (b) => {
        if (isFirstMonthBooking(b) && b.firstMonthRent != null) return b.firstMonthRent;
        return b.rentTotal || 0;
    };

    // The admin-editable amount for a booking: their override if they typed one, else the
    // computed default (firstMonthRent / rentTotal).
    const getBookingAmount = (b) => {
        const override = bookingAmounts[b._id];
        if (override !== undefined && override !== '') {
            const n = Number(override);
            return isNaN(n) ? 0 : n;
        }
        return bookingRent(b);
    };
    const updateBookingAmount = (id, value) => {
        setBookingAmounts(prev => ({ ...prev, [id]: value }));
    };

    const rentTotal = selectedBookings.reduce((sum, b) => sum + getBookingAmount(b), 0);
    const securityTotal = selectedBookings.filter(isFirstMonthBooking).reduce((sum, b) => sum + (b.securityAmount || 0), 0);
    const advanceTotal = selectedBookings.filter(isFirstMonthBooking).reduce((sum, b) => sum + (b.advanceAmount || 0), 0);
    const validExtraItems = extraLineItems.filter(item => item.description?.trim() && Number(item.amount));
    const extraTotal = validExtraItems.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
    const previewTotal = Math.max(0, rentTotal + securityTotal - advanceTotal + extraTotal);

    const toggleBooking = (id) => {
        setSelectedBookingIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const addExtraLineItem = () => {
        setExtraLineItems(prev => [...prev, { description: '', amount: '', type: INVOICE_LINE_ITEM_TYPES.OTHER }]);
    };
    const updateExtraLineItem = (idx, field, value) => {
        setExtraLineItems(prev => prev.map((item, i) => (i === idx ? { ...item, [field]: value } : item)));
    };
    const removeExtraLineItem = (idx) => {
        setExtraLineItems(prev => prev.filter((_, i) => i !== idx));
    };

    const canGoNext = () => {
        if (step === 0) return !!customer;
        if (step === 1) return !!selectedContractId;
        if (step === 2) return selectedBookingIds.size > 0;
        if (step === 3) return !!month && !!year;
        return true;
    };

    const handleNext = () => canGoNext() && setStep(s => Math.min(s + 1, STEP_LABELS.length - 1));
    const handleBack = () => setStep(s => Math.max(s - 1, presetCustomer ? 1 : 0));

    const handleCreate = async () => {
        if (previewTotal <= 0) {
            Notify('Invoice of amount 0 cannot be created. Add a line item with a nonzero amount first.', 'error');
            return;
        }
        try {
            const bookingAmountsPayload = {};
            selectedBookings.forEach(b => { bookingAmountsPayload[b._id] = getBookingAmount(b); });

            const res = await createMutation.mutateAsync({
                customerId: customer._id,
                contractId: selectedContractId,
                bookingIds: [...selectedBookingIds],
                bookingAmounts: bookingAmountsPayload,
                month: Number(month),
                year: Number(year),
                extraLineItems: validExtraItems.map(item => ({
                    description: item.description,
                    amount: Number(item.amount),
                    type: item.type,
                })),
                dueDate: dueDate || undefined,
                notes: notes || undefined,
            });

            const invoice = res.data?.invoice;
            const warnings = res.data?.warnings || [];
            warnings.forEach(w => Notify(w, 'warning'));
            Notify('Invoice created successfully', 'success');

            onClose();
            if (onSuccess) onSuccess(invoice);
            if (invoice?._id) navigate(`/invoices/${invoice._id}`);
        } catch (error) {
            Notify(error?.response?.data?.message || 'Failed to create invoice', 'error');
        }
    };

    const monthOptions = Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: moment().month(i).format('MMMM') }));
    const yearOptions = Array.from({ length: 4 }, (_, i) => moment().year() - i);
    const users = usersData?.users || [];

    return (
        <Dialog open={isOpen} handler={onClose} size="lg">
            <DialogHeader className="flex flex-col items-start gap-1">
                <span>Create Manual Invoice</span>
                <span className="text-xs font-normal text-gray-500">
                    Step {step + 1} of {STEP_LABELS.length}: {STEP_LABELS[step]}
                </span>
            </DialogHeader>
            <DialogBody className="max-h-[65vh] overflow-y-auto space-y-4">
                {/* Step 0: Customer */}
                {step === 0 && (
                    <div className="space-y-3">
                        <Input
                            label="Search Customer"
                            placeholder="Search by name, email or phone"
                            value={customerSearch}
                            onChange={(e) => setCustomerSearch(e.target.value)}
                            icon={<SearchIcon className="h-4 w-4" />}
                        />
                        <div className="h-[350px] overflow-y-auto border border-gray-100 rounded-lg">
                            {isLoadingUsers ? (
                                <div className="flex justify-center items-center h-full"><Spinner className="h-4 w-4" /></div>
                            ) : users.length === 0 ? (
                                <div className="flex justify-center items-center h-full text-sm text-gray-500">No customers found</div>
                            ) : (
                                <List>
                                    {users.map(u => (
                                        <ListItem
                                            key={u._id}
                                            selected={customer?._id === u._id}
                                            onClick={() => setCustomer(u)}
                                        >
                                            <div className="flex flex-col">
                                                <span className="text-sm font-medium">{convertToPresentableText(u.name)}</span>
                                                <span className="text-xs text-gray-500">{u.email}</span>
                                            </div>
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                        </div>
                    </div>
                )}

                {/* Step 1: Contract */}
                {step === 1 && (
                    <div className="space-y-3">
                        <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
                            <div className="text-sm font-medium">{convertToPresentableText(customer?.name)}</div>
                            <div className="text-xs text-gray-500">{customer?.email}</div>
                        </div>
                        {contractsLoading ? (
                            <div className="flex justify-center py-8"><Spinner className="h-4 w-4" /></div>
                        ) : contracts.length === 0 ? (
                            <div className="text-sm text-gray-500 text-center py-8">This customer has no contracts.</div>
                        ) : (
                            <List>
                                {contracts.map(c => (
                                    <ListItem
                                        key={c._id}
                                        selected={selectedContractId === c._id}
                                        onClick={() => { setSelectedContractId(c._id); setSelectedBookingIds(new Set()); }}
                                    >
                                        <div className="flex justify-between items-center w-full">
                                            <div>
                                                <div className="text-sm font-medium">Contract #{c.contractNumber || c._id.slice(-6)}</div>
                                                <div className="text-xs text-gray-500">
                                                    {c.startDate ? moment(c.startDate).format('MMM DD, YYYY') : 'N/A'}
                                                    {c.endDate ? ` – ${moment(c.endDate).format('MMM DD, YYYY')}` : ' – Ongoing'}
                                                </div>
                                            </div>
                                            <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                                                {convertToPresentableText(c.status)}
                                            </span>
                                        </div>
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </div>
                )}

                {/* Step 2: Bookings */}
                {step === 2 && (
                    <div className="space-y-3">
                        <div className="text-xs text-gray-500">
                            Billing period: {moment().month(month - 1).format('MMMM')} {year}
                            {' '}(change it in the next step if needed)
                        </div>
                        {approvedBookings.length === 0 ? (
                            <div className="text-sm text-gray-500 text-center py-8">
                                This contract has no approved bookings to invoice.
                            </div>
                        ) : (
                            <List>
                                {approvedBookings.map(b => {
                                    const coveredInvoiceNumber = coveredBookings.get(String(b._id));
                                    const isSelected = selectedBookingIds.has(b._id);
                                    return (
                                        <ListItem key={b._id} className="!p-0">
                                            <div className="flex items-start gap-3 w-full px-3 py-2">
                                                <Checkbox
                                                    checked={isSelected}
                                                    onChange={() => toggleBooking(b._id)}
                                                    className="h-4 w-4 mt-0.5"
                                                />
                                                <div className="flex-1">
                                                    <div className="flex justify-between items-center w-full gap-3">
                                                        <div className="cursor-pointer" onClick={() => toggleBooking(b._id)}>
                                                            <div className="text-sm font-medium">{bookingLabel(b)}</div>
                                                            <div className="text-xs text-gray-500">
                                                                Started {moment(b.startDate).format('MMM DD, YYYY')}
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-1 flex-shrink-0">
                                                            <span className="text-xs text-gray-400">PKR</span>
                                                            <input
                                                                type="number"
                                                                className="w-24 border border-gray-300 rounded-lg px-2 py-1 text-sm text-right"
                                                                value={bookingAmounts[b._id] ?? bookingRent(b)}
                                                                onChange={(e) => updateBookingAmount(b._id, e.target.value)}
                                                            />
                                                        </div>
                                                    </div>
                                                    {coveredInvoiceNumber && (
                                                        <div className="flex items-center gap-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1.5">
                                                            <AlertTriangleIcon className="h-3 w-3 flex-shrink-0" />
                                                            Already invoiced on {coveredInvoiceNumber} for this period — selecting it will bill it again.
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </ListItem>
                                    );
                                })}
                            </List>
                        )}
                    </div>
                )}

                {/* Step 3: Billing Period & Extras */}
                {step === 3 && (
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-sm text-gray-600 mb-1 block">Month</label>
                                <select
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                    value={month}
                                    onChange={(e) => setMonth(parseInt(e.target.value))}
                                >
                                    {monthOptions.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-sm text-gray-600 mb-1 block">Year</label>
                                <select
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                    value={year}
                                    onChange={(e) => setYear(parseInt(e.target.value))}
                                >
                                    {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                                </select>
                            </div>
                        </div>

                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="text-sm text-gray-600">Additional Line Items</label>
                                <button
                                    type="button"
                                    className="flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-800"
                                    onClick={addExtraLineItem}
                                >
                                    <PlusIcon className="h-3.5 w-3.5" /> Add Item
                                </button>
                            </div>
                            {extraLineItems.length === 0 ? (
                                <div className="text-xs text-gray-400">No additional charges.</div>
                            ) : (
                                <div className="space-y-2">
                                    {extraLineItems.map((item, idx) => (
                                        <div key={idx} className="flex items-center gap-2">
                                            <select
                                                className="border border-gray-300 rounded-lg px-2 py-2 text-xs w-32"
                                                value={item.type}
                                                onChange={(e) => updateExtraLineItem(idx, 'type', e.target.value)}
                                            >
                                                <option value={INVOICE_LINE_ITEM_TYPES.OTHER}>Other</option>
                                            </select>
                                            <input
                                                type="text"
                                                placeholder="Description"
                                                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                                value={item.description}
                                                onChange={(e) => updateExtraLineItem(idx, 'description', e.target.value)}
                                            />
                                            <input
                                                type="number"
                                                placeholder="Amount"
                                                className="w-28 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                                value={item.amount}
                                                onChange={(e) => updateExtraLineItem(idx, 'amount', e.target.value)}
                                            />
                                            <button
                                                type="button"
                                                className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                                                onClick={() => removeExtraLineItem(idx)}
                                            >
                                                <Trash2Icon className="h-4 w-4 text-red-500" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div>
                            <label className="text-sm text-gray-600 mb-1 block">Due Date</label>
                            <input
                                type="date"
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                value={dueDate}
                                onChange={(e) => setDueDate(e.target.value)}
                            />
                            <p className="text-[10px] text-gray-400 mt-1">Leave blank to use the default due-date setting.</p>
                        </div>

                        <div>
                            <label className="text-sm text-gray-600 mb-1 block">Notes</label>
                            <textarea
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                rows={2}
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                            />
                        </div>
                    </div>
                )}

                {/* Step 4: Preview */}
                {step === 4 && (
                    <div className="space-y-4">
                        <div className="rounded-lg border border-gray-200 divide-y">
                            {selectedBookings.map(b => (
                                <div key={b._id} className="flex justify-between items-center px-3 py-2 text-sm">
                                    <span>{bookingLabel(b)} - Monthly Rent</span>
                                    <span className="font-medium">PKR {formatNumberWithCommas(getBookingAmount(b))}</span>
                                </div>
                            ))}
                            {securityTotal > 0 && (
                                <div className="flex justify-between items-center px-3 py-2 text-sm">
                                    <span>Security Deposit</span>
                                    <span className="font-medium">PKR {formatNumberWithCommas(securityTotal)}</span>
                                </div>
                            )}
                            {advanceTotal > 0 && (
                                <div className="flex justify-between items-center px-3 py-2 text-sm">
                                    <span>Advance Deduction</span>
                                    <span className="font-medium">- PKR {formatNumberWithCommas(advanceTotal)}</span>
                                </div>
                            )}
                            {validExtraItems.map((item, idx) => (
                                <div key={idx} className="flex justify-between items-center px-3 py-2 text-sm">
                                    <span>{item.description}</span>
                                    <span className="font-medium">PKR {formatNumberWithCommas(item.amount)}</span>
                                </div>
                            ))}
                            <div className="flex justify-between items-center px-3 py-2 text-sm font-bold bg-gray-50">
                                <span>Total</span>
                                <span>PKR {formatNumberWithCommas(previewTotal)}</span>
                            </div>
                        </div>
                        {previewTotal <= 0 && (
                            <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
                                <AlertTriangleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                                <span>
                                    This invoice totals PKR 0 — it can't be created. Go back and set a nonzero
                                    amount on a booking, or add an extra line item.
                                </span>
                            </div>
                        )}
                        {selectedBookings.some(b => coveredBookings.has(String(b._id))) && (
                            <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                                <AlertTriangleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                                <div>
                                    <div className="font-semibold mb-1">
                                        These bookings already have an invoice for {moment().month(month - 1).format('MMMM')} {year}:
                                    </div>
                                    <ul className="list-disc list-inside space-y-0.5">
                                        {selectedBookings.filter(b => coveredBookings.has(String(b._id))).map(b => (
                                            <li key={b._id}>{bookingLabel(b)} — already on {coveredBookings.get(String(b._id))}</li>
                                        ))}
                                    </ul>
                                    <div className="mt-1">You can still proceed, but this will bill them a second time unless intentional.</div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </DialogBody>
            <DialogFooter className="flex justify-between">
                <div>
                    {step > (presetCustomer ? 1 : 0) && (
                        <Button variant="text" color="gray" onClick={handleBack} className="flex items-center gap-2">
                            <ArrowLeftIcon className="h-4 w-4" /> Back
                        </Button>
                    )}
                </div>
                <div className="flex gap-2">
                    <Button variant="text" color="gray" onClick={onClose}>Cancel</Button>
                    {step < STEP_LABELS.length - 1 ? (
                        <Button
                            variant="gradient" color="gray"
                            onClick={handleNext}
                            disabled={!canGoNext()}
                            className="flex items-center gap-2 rounded-full"
                        >
                            Next <ArrowRightIcon className="h-4 w-4" />
                        </Button>
                    ) : (
                        <Button
                            variant="gradient" color="green"
                            onClick={handleCreate}
                            disabled={createMutation.isLoading || previewTotal <= 0}
                            className="flex items-center gap-2 rounded-full"
                        >
                            {createMutation.isLoading && <Spinner className="h-4 w-4" />}
                            Create Draft Invoice
                        </Button>
                    )}
                </div>
            </DialogFooter>
        </Dialog>
    );
};

export default CreateInvoiceModal;
